import { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { EmptyStateMessage, Section, SectionCard, SectionHeader, SubsectionHeader } from '@/components/common';
import { stripHtml } from '@/utils/stripHtml';
import { DevelopmentNeedRow } from './DevelopmentNeedRow';
import { useUpdateForwardLookingPlan } from '@/queries/useAppraisalDetail';
import type { AppraisalDetailResponse, ForwardLookingPlanItem } from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';
import { readOnlySectionVariant } from '@/auth/sectionReadOnlyStyle';

interface ForwardLookingPlanSectionProps {
  appraisalDetail: AppraisalDetailResponse;
  permissions: RecordAccess;
  validationTriggered?: boolean;
}

const MIN_ITEMS = 3;

function createEmptyItem(orderIndex: number): ForwardLookingPlanItem {
  return {
    developmentNeed: '',
    actionToResolve: '',
    orderIndex,
  };
}

function ensureMinItems(items: ForwardLookingPlanItem[]): ForwardLookingPlanItem[] {
  const result = [...items];
  while (result.length < MIN_ITEMS) {
    result.push(createEmptyItem(result.length));
  }
  return result;
}

export function ForwardLookingPlanSection({
  appraisalDetail,
  permissions,
  validationTriggered = false,
}: ForwardLookingPlanSectionProps) {
  const sectionAccess = permissions.sections.forwardLookingPlan;
  const canEdit = sectionAccess?.access === 'edit';
  const readOnlyVariant = readOnlySectionVariant('forwardLookingPlan', permissions.role);

  const updateMutation = useUpdateForwardLookingPlan(String(appraisalDetail.id));

  const serverItems = appraisalDetail.forwardLookingPlan;
  const serverKey = useMemo(() => JSON.stringify(serverItems ?? []), [serverItems]);

  const [localItems, setLocalItems] = useState<ForwardLookingPlanItem[]>(() =>
    ensureMinItems(serverItems ?? [])
  );

  // Reset local state when server data changes (after mutation success / cache invalidation)
  const [prevKey, setPrevKey] = useState(serverKey);
  if (serverKey !== prevKey) {
    setPrevKey(serverKey);
    setLocalItems(ensureMinItems(serverItems ?? []));
  }

  const hasError = useMemo(
    () =>
      validationTriggered &&
      localItems.some(
        (item) => !stripHtml(item.developmentNeed) || !stripHtml(item.actionToResolve)
      ),
    [validationTriggered, localItems]
  );

  // Blocks blur-triggered saves from the moment a mutation fires until
  // fresh server data arrives AND editor setContent effects have settled.
  // This prevents the 409 caused by setContent triggering a spurious blur
  // with the old rowVersion while the refetch is still in flight.
  const saveLockRef = useRef(false);

  // Unlock saves after server data arrives + one frame (so setContent effects settle)
  useEffect(() => {
    if (!saveLockRef.current) return;
    const id = requestAnimationFrame(() => {
      saveLockRef.current = false;
    });
    return () => cancelAnimationFrame(id);
  }, [serverKey]);

  const handleChange = (index: number, field: 'developmentNeed' | 'actionToResolve', value: string) => {
    const newItems = [...localItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setLocalItems(newItems);
  };

  const handleBlur = () => {
    if (saveLockRef.current) return;
    saveLockRef.current = true;
    updateMutation.mutate({ items: localItems, rowVersion: appraisalDetail.rowVersion });
  };

  const handleAdd = () => {
    const newItems = [...localItems, createEmptyItem(localItems.length)];
    setLocalItems(newItems);
    saveLockRef.current = true;
    updateMutation.mutate({ items: newItems, rowVersion: appraisalDetail.rowVersion });
  };

  const handleDelete = (index: number) => {
    if (localItems.length <= MIN_ITEMS) return;

    const newItems = localItems
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, orderIndex: i }));

    setLocalItems(newItems);
    saveLockRef.current = true;
    updateMutation.mutate({ items: newItems, rowVersion: appraisalDetail.rowVersion });
  };

  const canDelete = localItems.length > MIN_ITEMS;

  const itemsToDisplay = canEdit
    ? localItems
    : localItems.filter(
        (item) => stripHtml(item.developmentNeed) !== '' || stripHtml(item.actionToResolve) !== ''
      );

  const showEmptyMessage = !canEdit && itemsToDisplay.length === 0;

  const canView = sectionAccess?.access === 'read' || sectionAccess?.access === 'edit';
  if (!canView) {
    return null;
  }

  return (
    <Section>
      <SectionHeader error={hasError}>
        FORWARD LOOKING PLAN
      </SectionHeader>

      <SectionCard error={hasError}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Progress against identified development needs
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              // Prevent the click from blurring a focused editor: the blur would
              // fire its own save with the same rowVersion and race the add → 409.
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleAdd}
              size="small"
            >
              CREATE ANOTHER NEED
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <SubsectionHeader>DEVELOPMENT NEED</SubsectionHeader>
          </Box>
          <Box sx={{ flex: 1 }}>
            <SubsectionHeader>ACTIONS</SubsectionHeader>
          </Box>
          {canEdit && <Box sx={{ width: 40 }} />}
        </Box>

        {showEmptyMessage ? (
          <EmptyStateMessage message="No development needs recorded." />
        ) : (
          itemsToDisplay.map((item, index) => (
            <DevelopmentNeedRow
              key={item.orderIndex}
              developmentNeed={item.developmentNeed}
              actionToResolve={item.actionToResolve}
              onChange={(field, value) => handleChange(index, field, value)}
              onBlur={handleBlur}
              onDelete={() => handleDelete(index)}
              canEdit={canEdit}
              canDelete={canDelete}
              readOnlyVariant={readOnlyVariant}
              striped={index % 2 === 0}
              error={{
                developmentNeed:
                  validationTriggered && !stripHtml(item.developmentNeed),
                actionToResolve:
                  validationTriggered && !stripHtml(item.actionToResolve),
              }}
            />
          ))
        )}
      </SectionCard>
    </Section>
  );
}

export default ForwardLookingPlanSection;
